import { Modal, Switch, Tag, Tooltip } from "antd";
import Table, { ColumnsType } from "antd/lib/table";
import Draggable from "react-draggable";

import { useWebUserList } from "./index.hook";
import { TableHelper } from "../../../../util/TableHelper";
import styles from "./index.module.less";
import Loading from "../../../common/Loading";
import ResetPasswordByAdmin from "../../ResetPasswordByAdmin";
import Authentication from "../../../../util/Authentication";
import LocalStorageUtil from "../../../../util/LocalStorageUtil";
import { LocalStorageConstants } from "../../../../constants/ApplicationConstants/LocalStorageConstants";
import { Modules, Operations } from "../../../../constants/DatabaseConstants/MasterConstant";
import { UserRoleDATA } from "../../../../constants/DatabaseConstants/AuthConstants";
import { useReactDraggable } from "../../../../hooks/useReactDraggable";

import type { UserInterface } from "../../../../shared/interface/auth/User.interface";
import type { GetAllUserResponse } from "../../../../shared/interface/UserManagement/user.interface";

const WebUserList = (props: { searchedText: string }) => {
  /* CONSTANT */
  const userData = LocalStorageUtil.localStorageGetItem(LocalStorageConstants.USER_DATA) as UserInterface;

  const config = useWebUserList({ searchedText: props.searchedText });

  const { bounds, onStart } = useReactDraggable();

  const renderText = (text: string | number) => {
    return <div className="wordWrapWordBreak">{TableHelper.getValue(text)}</div>;
  };

  const renderNumberValue = (text: string | number) => {
    return <div className="gx-text-right">{TableHelper.getValue(text)}</div>;
  };

  const columns: ColumnsType<GetAllUserResponse> = [
    {
      align: "left",
      title: "Sr No.",
      dataIndex: "sr_no",
      key: "sr_no",
      render: renderNumberValue,
      sorter: {
        compare: TableHelper.valueCompare("id")
      },
      width: "5%"
    },
    {
      align: "left",
      title: "Customer",
      dataIndex: "customer_name",
      key: "customer_name",
      render: renderText,
      sorter: {
        compare: TableHelper.valueCompare("customer_name")
      }
    },
    {
      align: "left",
      title: "Username",
      dataIndex: "username",
      key: "username",
      render: (text, record) => (
        <Tooltip title="View User Details">
          <div onClick={config?.handleViewUser.bind(this, record)} style={{ wordWrap: "break-word", wordBreak: "break-word" }} className={`${record?.id === config?.userId ? styles["modal__description"] : styles["view_user_text_color"]} `}>
            {TableHelper.getValue(text)}
          </div>
        </Tooltip>
      ),
      sorter: {
        compare: TableHelper.valueCompare("username")
      }
    },
    {
      align: "left",
      title: "Full Name",
      dataIndex: "full_name",
      key: "full_name",
      render: renderText,
      sorter: {
        compare: TableHelper.valueCompare("full_name")
      }
    },
    {
      align: "left",
      title: "Email Address",
      dataIndex: "email_address",
      key: "email_address",
      render: renderText,
      sorter: {
        compare: TableHelper.valueCompare("email_address")
      }
    },
    {
      align: "left",
      title: "Mobile Number",
      dataIndex: "mobile_number",
      key: "mobile_number",
      render: renderNumberValue,
      sorter: {
        compare: TableHelper.valueCompare("mobile_number")
      }
    },
    {
      align: "left",
      title: "Role",
      dataIndex: "role_name",
      key: "role_name",
      render: renderText,
      sorter: {
        compare: TableHelper.valueCompare("role_name")
      }
    },
    {
      align: "left",
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      sorter: (a, b) => (a.is_active === false ? -1 : 1),
      render: (text, record) => <div className="table-text-wrap">{text === true ? <Tag color="success">Active</Tag> : <Tag color="error">In-Active</Tag>}</div>
    },
    {
      align: "left",
      title: "Actions",
      fixed: "right",
      key: "actions",
      width: "13%",
      render: (text, record) => {
        /* check edit access */
        const editAccess = record?.role_name !== UserRoleDATA.PARI_ADMIN && Authentication.hasModuleAndOperationAccess(userData, Modules.WEB_USER, Operations.UPDATE);

        /* check reset password access */
        const resetPasswordAccess = record?.role_name !== UserRoleDATA.PARI_ADMIN && Authentication.hasModuleAndOperationAccess(userData, Modules.WEB_USER_RESET_PASSWORD, Operations.READ);

        /* check delete access */
        const deleteAccess = record?.role_name !== UserRoleDATA.PARI_ADMIN && Authentication.hasModuleAndOperationAccess(userData, Modules.WEB_USER, Operations.DELETE);
        return (
          <div className={`${styles["actionAllDevice"]}`}>
            {editAccess && <Tooltip title="Edit User">{record?.is_active ? <i onClick={config?.handleEditUser.bind(this, record)} className={`gx-link icon icon-edit ${styles["action-icon"]} `} /> : <i className={`icon icon-edit icon-disabled ${styles["action-icon"]} `} />}</Tooltip>}

            {resetPasswordAccess && (
              <Tooltip title="Reset Password" getPopupContainer={(trigger) => trigger.parentElement || document.body}>
                {record?.is_active ? (
                  <span className="gx-link" onClick={() => config?.handleResetPassword(record)}>
                    <i className={`icon icon-reset-password ${styles["action-icon"]}`} />
                  </span>
                ) : (
                  <span>
                    <i className={`icon icon-reset-password icon-disabled ${styles["action-icon"]}`} />
                  </span>
                )}
              </Tooltip>
            )}

            {deleteAccess && (
              <Tooltip title={record?.is_active ? "Active" : "In-Active"} getPopupContainer={(trigger) => trigger.parentElement || document.body}>
                <span className={"gx-link"}>
                  <Switch checked={record?.is_active} onClick={config?.handleDeleteUser.bind(this, record)} className={styles["action-icon"]} />
                </span>
              </Tooltip>
            )}
          </div>
        );
      }
    }
  ];

  return (
    <div>
      {config?.loading && <Loading />}

      {!config?.loading && <Table className="gx-table-responsive" size="small" columns={columns} dataSource={config?.filteredTableData} pagination={config?.pagination} onChange={config?.handleChangePagination} rowKey={(record) => record.sr_no} bordered />}

      <Modal
        title={<div className="modal-container">{`Active/In-Active Confirmation`}</div>}
        open={config?.showDeleteModal}
        onCancel={config.handleCancel}
        onOk={config.handleOk}
        okText="Yes"
        cancelText="No"
        width="580px"
        destroyOnClose
        maskClosable={false}
        wrapClassName="delete-modal"
        modalRender={(modal) => (
          <Draggable bounds={bounds} handle=".ant-modal-header" onStart={(event, uiData) => onStart(event as any, uiData)}>
            {modal}
          </Draggable>
        )}
        okButtonProps={{
          disabled: config?.saving,
          loading: config?.saving
        }}
      >
        <h4>
          {`Are you sure you want to ${config.isActive ? "inactivate" : "activate"} the account for user`} <span className={styles["modal__description"]}>{config.userName}</span> ?
        </h4>
      </Modal>

      <ResetPasswordByAdmin {...config?.ResetPasswordProps} />
    </div>
  );
};

export default WebUserList;
